import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import {
  TEAM_KEYS,
  TelephonyNumber,
  telephonyApi,
} from '../../../Telephony/telephonyApi';
import { isTelephonyManager } from '../../../Telephony/roles';
import { useEmailAccessConfig } from '../../../utils/emailAccessConfig';
import { Page, PageHeader, Card, Button, Badge, Field, TextInput, Select, Table, Loader, EmptyState } from '../../../ui';
import TelephonyAdminOnly from './AdminOnly';
import './telephony.scss';

/**
 * Telephony → Phone Numbers: the caller-ID pool. These rows ARE the routing
 * data — which vendor carries a call, which caller ID a country sees, and
 * how rotation spreads. Deactivating removes a number from dialing instantly.
 */
const emptyForm = { e164: '', provider: 'PLIVO', teamKey: '', label: '' };

const TelephonyNumbersPage: React.FC = () => {
  useEmailAccessConfig();
  const [teamKeys, setTeamKeys] = useState<string[]>([...TEAM_KEYS]);
  useEffect(() => {
    telephonyApi<{ key: string; isActive: boolean }[]>('/v1/teams')
      .then((teams) =>
        setTeamKeys(teams.filter((t) => t.isActive).map((t) => t.key)),
      )
      .catch(() => undefined); // static fallback already in state
  }, []);

  const [numbers, setNumbers] = useState<TelephonyNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    telephonyApi<TelephonyNumber[]>('/v1/numbers')
      .then(setNumbers)
      .catch((e) => toast.error(`Numbers: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const addNumber = async () => {
    if (!form.e164) { toast.warn('Number (E.164) is required'); return; }
    setSaving(true);
    try {
      await telephonyApi('/v1/numbers', {
        method: 'POST',
        body: JSON.stringify({
          e164: form.e164,
          provider: form.provider,
          teamKey: form.teamKey || undefined,
          label: form.label || undefined,
        }),
      });
      toast.success(`${form.e164} added to the pool`);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (n: TelephonyNumber) => {
    try {
      await telephonyApi(`/v1/numbers/${n.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !n.isActive }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  // Marks the number as WhatsApp-calling-capable (it must ALSO be enabled on
  // the Plivo-linked WABA in their console — this flag just tells routing
  // which number WhatsApp calls present).
  const toggleWhatsapp = async (n: TelephonyNumber) => {
    try {
      await telephonyApi(`/v1/numbers/${n.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ whatsappEnabled: !n.whatsappEnabled }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const setTeam = async (n: TelephonyNumber, teamKey: string) => {
    try {
      await telephonyApi(`/v1/numbers/${n.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ teamKey }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (!isTelephonyManager()) {
    return <TelephonyAdminOnly title="Phone Numbers" />;
  }

  const vendorOptions = [
    { value: 'PLIVO', label: 'PLIVO' },
    { value: 'TWILIO', label: 'TWILIO' },
  ];
  const teamOptions = (emptyLabel: string) => [
    { value: '', label: emptyLabel },
    ...teamKeys.map((t) => ({ value: t, label: t })),
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Phone Numbers"
        subtitle={loading ? 'Loading numbers…' : `${numbers.length} numbers · ${numbers.filter((n) => n.isActive).length} active`}
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <Card
        title="Register number"
        subtitle="The number must already be purchased at the vendor — this adds it to the routing pool. Country and vendor decide which customers see it."
      >
        <div className="tel-form-row">
          <Field label="Number (E.164)">
            {(ctl) => (
              <TextInput {...ctl} placeholder="+44…" value={form.e164} onChange={(e) => setForm({ ...form, e164: e.target.value })} />
            )}
          </Field>
          <Field label="Vendor">
            {(ctl) => (
              <Select {...ctl} options={vendorOptions} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
            )}
          </Field>
          <Field label="Team (optional)">
            {(ctl) => (
              <Select {...ctl} options={teamOptions('— shared pool —')} value={form.teamKey} onChange={(e) => setForm({ ...form, teamKey: e.target.value })} />
            )}
          </Field>
          <Field label="Label">
            {(ctl) => (
              <TextInput {...ctl} placeholder="e.g. UK Twilio 1" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            )}
          </Field>
          <div className="tel-form-row__submit">
            <Button variant="primary" loading={saving} onClick={addNumber}>
              {saving ? 'Saving…' : 'Add to pool'}
            </Button>
          </div>
        </div>
      </Card>

      <Card flush title="Number pool">
        {loading ? (
          <Loader label="Loading numbers…" />
        ) : numbers.length === 0 ? (
          <div className="tel-flush-pad">
            <EmptyState title="No numbers in the pool" description="Register a purchased number above to start routing calls through it." />
          </div>
        ) : (
          <Table caption="Caller-ID number pool">
            <thead>
              <tr>
                <th>Number</th><th>Country</th><th>Vendor</th><th>Purpose</th>
                <th>WhatsApp</th>
                <th>Team</th><th>Label</th><th>Last used</th><th>Status</th><th><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {numbers.map((n) => (
                <tr key={n.id} className={n.isActive ? undefined : 'tel-inactive'}>
                  <td className="text-nowrap"><span className="ds-strong">{n.e164}</span></td>
                  <td>{n.country}</td>
                  <td>{n.provider}</td>
                  <td><span className="ds-text-muted">{n.purpose}</span></td>
                  <td>
                    <div className="form-check form-switch tel-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        aria-label={`WhatsApp calling for ${n.e164}`}
                        disabled={n.provider !== 'PLIVO'}
                        title={
                          n.provider !== 'PLIVO'
                            ? 'WhatsApp calling runs on Plivo-linked WABA numbers only'
                            : n.whatsappEnabled
                              ? 'WhatsApp calling on — calls present this number'
                              : 'Mark as WhatsApp-calling-enabled (must match the Plivo console toggle)'
                        }
                        checked={!!n.whatsappEnabled}
                        onChange={() => toggleWhatsapp(n)}
                      />
                      {n.whatsappEnabled ? (
                        <i className="fa fa-whatsapp tel-whatsapp-icon" aria-hidden />
                      ) : null}
                    </div>
                  </td>
                  <td className="tel-team-cell">
                    <Select
                      size="sm"
                      aria-label={`Team for ${n.e164}`}
                      options={teamOptions('— shared —')}
                      value={n.team?.key ?? ''}
                      onChange={(e) => setTeam(n, e.target.value)}
                    />
                  </td>
                  <td><span className="ds-text-subtle">{n.label ?? '—'}</span></td>
                  <td className="text-nowrap"><span className="ds-text-muted">{n.lastUsedAt ? new Date(n.lastUsedAt).toLocaleString() : 'never'}</span></td>
                  <td>
                    <Badge tone={n.isActive ? 'success' : 'danger'}>
                      {n.isActive ? 'active' : 'inactive'}
                    </Badge>
                  </td>
                  <td>
                    <Button size="sm" variant="secondary" onClick={() => toggleActive(n)}>
                      {n.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
};

export default TelephonyNumbersPage;
